import type { AdditionalFormat, TranscriptionResponse } from "@ui/components/demos/STT/types";

import { env } from "@/env";

const SERVER_URL = env.NEXT_PUBLIC_XI_SERVER_URL;
const DEFAULT_STT_MODEL_ID = "scribe_v2";

export interface GenerateSttParams {
  diarize?: boolean;
  file: File;
  hcaptcha_token?: null | string;
  language_code?: null | string;
  model_id?: string;
  num_speakers?: null | string;
  signal?: AbortSignal;
  tag_audio_events?: boolean;
  with_additional_formats?: boolean;
}

const additionalFormats = [
  {
    format: "segmented_json",
  },
  {
    format: "txt",
  },
  {
    format: "html",
  },
  {
    format: "srt",
  },
  {
    format: "pdf",
  },
  {
    format: "docx",
  },
];

export const generateStt = async ({
  file,
  tag_audio_events = true,
  language_code = null,
  num_speakers = null,
  diarize = true,
  signal,
  with_additional_formats = true,
  hcaptcha_token,
  model_id = DEFAULT_STT_MODEL_ID,
}: GenerateSttParams): Promise<TranscriptionResponse> => {
  const url = new URL(`${SERVER_URL}/v1/speech-to-text`);
  url.searchParams.set("allow_unauthenticated", "1");
  if (hcaptcha_token) {
    url.searchParams.set("hcaptcha_token", hcaptcha_token);
  }

  const formData = new FormData();

  formData.append("file", file);
  formData.append("model_id", model_id);
  formData.append("tag_audio_events", tag_audio_events.toString());
  with_additional_formats && formData.append("additional_formats", JSON.stringify(additionalFormats));

  // optional params
  language_code && formData.append("language_code", language_code);
  num_speakers && formData.append("num_speakers", num_speakers);
  diarize && formData.append("diarize", diarize.toString());

  const response = await fetch(url.toString(), {
    method: "POST",
    body: formData,
    signal,
  });

  if (!response.ok) {
    const { detail } = await response.json();
    throw detail;
  }

  const res = await response.json();
  // API returns some null values sometimes so we filter them out.
  res.additional_formats = res.additional_formats?.filter((f: AdditionalFormat | null) => !!f) || [];
  return res;
};

export interface GenerateSttFromUrlResponse {
  chunk: TranscriptionResponse;
  source_title: string;
  transcription_id: string;
  url: string;
}

// Download and transcribe is slow, the user will have to wait for a long time if the audio is too long.
const MAX_DEMO_DURATION_SECONDS = 60 * 5;

// Helper function to normalize URLs
const normalizeUrl = (url: string): string => {
  const lowerUrl = url.toLowerCase();
  if (!lowerUrl.startsWith("http://") && !lowerUrl.startsWith("https://")) {
    return `https://${url}`;
  }
  if (lowerUrl.startsWith("http://")) {
    return url.replace(/^http:\/\//i, "https://");
  }
  return url;
};

export const generateSttFromUrl = async ({
  url: urlString,
  signal,
  with_additional_formats = true,
  hcaptcha_token,
  model_id = DEFAULT_STT_MODEL_ID,
}: {
  hcaptcha_token?: null | string;
  model_id?: string;
  signal?: AbortSignal;
  url: string;
  with_additional_formats?: boolean;
}): Promise<GenerateSttFromUrlResponse> => {
  // Normalize the URL before sending to API
  const normalizedUrl = normalizeUrl(urlString);

  const url = new URL(`${SERVER_URL}/v1/speech-to-text/url`);
  url.searchParams.set("allow_unauthenticated", "1");
  if (hcaptcha_token) {
    url.searchParams.set("hcaptcha_token", hcaptcha_token);
  }

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      url: normalizedUrl,
      model_id: model_id,
      tag_audio_events: true,
      diarize: true,
      // This will limit download size when using URLs, not only the STT processing.
      end_time: MAX_DEMO_DURATION_SECONDS,
      ...(with_additional_formats
        ? {
            additional_formats: additionalFormats,
          }
        : {}),
    }),
    signal,
  });

  if (!response.ok) {
    const { detail } = await response.json();
    throw detail;
  }

  const res = await response.json();

  // API returns some null values sometimes so we filter them out.
  res.additional_formats = res.additional_formats?.filter((f: AdditionalFormat | null) => !!f) || [];

  res.url = `${SERVER_URL}/v1/speech-to-text/url/${res.transcription_id}/file`;
  return res;
};

export interface GetPresignedUrlFromTranscriptionIdResponse {
  expires_in_seconds: number;
  presigned_url: string;
  transcription_id: string;
}

export const getPresignedUrlFromTranscriptionid = async ({
  id,
}: {
  id: string;
}): Promise<GetPresignedUrlFromTranscriptionIdResponse> => {
  const url = new URL(`${SERVER_URL}/v1/speech-to-text/url/${id}/file`);
  url.searchParams.set("allow_unauthenticated", "1");
  const response = await fetch(url.toString());

  if (!response.ok) {
    const { detail } = await response.json();
    throw detail;
  }

  return await response.json();
};

interface ErrorConfig {
  description: string;
  title: string;
}

export type ErrorCode = "max_character_limit_exceeded" | "quota_exceeded" | "system_busy" | "invalid_file_size";

export const STT_ERROR_MAP: Record<ErrorCode, ErrorConfig> = {
  quota_exceeded: {
    title: "Usage Limit Reached",
    description:
      "You've reached your free usage limit. Create an account to continue using the service with additional features.",
  },
  system_busy: {
    title: "Service Temporarily Unavailable",
    description: "Our servers are experiencing high demand right now. Please wait a few minutes and try again.",
  },
  max_character_limit_exceeded: {
    title: "Audio Length Exceeds Limit",
    description: "Your audio file is too long for processing. Please upload a shorter audio file (maximum 10 minutes).",
  },
  invalid_file_size: {
    title: "File Size Exceeds Limit",
    description: "The file you uploaded is too large, only uploads up to 300mb are allowed.",
  },
};

export const DEFAULT_ERROR: ErrorConfig = {
  title: "Unable to Process Request",
  description:
    "An unexpected error occurred while processing your audio. Please try again or contact support if the issue persists.",
};
